import PalommaLogoSmall from "@/assets/PalommaLogoSmall.jpg";
import { Box, Flex, HStack, Image, Text, VStack } from "@chakra-ui/react";
import { AlertTriangle, Info } from "lucide-react";
import { CompletedState } from "./components/CompletedState";
import { EmailModal } from "./components/EmailModal";
import { OnboardingFooter } from "./components/OnboardingFooter";
import { OnboardingSidebar } from "./components/OnboardingSidebar";
import { ResumeLinkBanner } from "./components/ResumeLinkBanner";
import { STEPS } from "./constants";
import { Step1BasicInfo } from "./steps/Step1BasicInfo";
import { Step2Profile } from "./steps/Step2Profile";
import { Step3Collection } from "./steps/Step3Collection";
import { Step4Experience } from "./steps/Step4Experience";
import { Step5Billing } from "./steps/Step5Billing";
import { Step6Compliance } from "./steps/Step6Compliance";
import { useOnboarding } from "./useOnboarding";

export default function OnboardingContainer() {
  const o = useOnboarding();

  return (
    <Flex
      h="100dvh"
      flexDir="column"
      fontFamily="body"
      bg="white"
      overflow="hidden"
    >
      <Flex
        flex="1"
        flexDir={{ base: "column", lg: "row" }}
        overflow="hidden"
        minH={0}
        position="relative"
      >
        <OnboardingSidebar
          currentStep={o.currentStep}
          isCompleted={o.isCompleted}
          applicationStatus={o.applicationStatus}
        />

        <Flex
          flex="1"
          flexDir="column"
          bg="white"
          overflow="hidden"
          minH={0}
          position="relative"
        >
          <Box
            display={{ base: "block", lg: "none" }}
            borderBottom="1px solid"
            borderColor="gray.100"
            px={5}
            py={4}
          >
            <Flex maxW="4xl" mx="auto" alignItems="center" gap={3}>
              <Box
                h={10}
                w={10}
                borderRadius="lg"
                bg="palomma.50"
                display="flex"
                alignItems="center"
                justifyContent="center"
                flexShrink={0}
              >
                <Image
                  src={PalommaLogoSmall}
                  alt="Palomma"
                  h={5}
                  w={5}
                  objectFit="contain"
                />
              </Box>
              <Box minW={0}>
                <Text
                  fontSize="xs"
                  fontWeight="semibold"
                  color="gray.500"
                  textTransform="uppercase"
                  letterSpacing="wider"
                >
                  Paso {o.currentStep} de {STEPS.length}
                </Text>
                <Text fontSize="lg" fontWeight="semibold" color="gray.900">
                  {STEPS[Math.min(o.currentStep - 1, STEPS.length - 1)].title}
                </Text>
              </Box>
            </Flex>
          </Box>

          <Box
            flex="1"
            overflowY="auto"
            minH={0}
            px={{ base: 6, sm: 8, lg: 12 }}
            py={{ base: 8, sm: 10 }}
          >
            <Box maxW="4xl" mx="auto">
              {o.isLoading ? (
                <Flex alignItems="center" justifyContent="center" py={24}>
                  <VStack gap={4} color="gray.400">
                    <Box
                      w={8}
                      h={8}
                      borderWidth="2px"
                      borderColor="gray.200"
                      borderTopColor="gray.500"
                      borderRadius="full"
                      animation="spin 0.8s linear infinite"
                    />
                    <Text fontSize="sm">Cargando solicitud...</Text>
                  </VStack>
                </Flex>
              ) : (
                <>
                  {o.error && (
                    <Box
                      mb={6}
                      borderRadius="xl"
                      borderWidth="1px"
                      borderColor="red.200"
                      bg="red.50"
                      p={4}
                    >
                      <Text fontSize="sm" color="red.600" lineHeight="tall">
                        {o.error}
                      </Text>
                    </Box>
                  )}

                  {o.applicationId && !o.isCompleted && (
                    <Box mb={6}>
                      <ResumeLinkBanner applicationId={o.applicationId} />
                    </Box>
                  )}

                  {o.isReturned && !o.isCompleted && (
                    <Box
                      mb={8}
                      spaceY={4}
                      borderRadius="xl"
                      borderWidth="1px"
                      borderColor="orange.200"
                      bg="orange.50"
                      p={5}
                    >
                      <HStack gap={3} alignItems="flex-start">
                        <Box flexShrink={0} mt={1}>
                          <AlertTriangle
                            size={20}
                            color="var(--chakra-colors-orange-600)"
                          />
                        </Box>
                        <Box spaceY={1}>
                          <Text fontWeight="medium" color="orange.800">
                            Más información solicitada
                          </Text>
                          <Text
                            fontSize="sm"
                            color="orange.700"
                            lineHeight="tall"
                          >
                            Necesitamos un poco más de información para terminar
                            el registro de tu comercio. Puedes navegar
                            libremente por todos los pasos para completar y
                            corregir la información. Para que tu solicitud sea
                            revisada, debes enviar la solicitud desde el último
                            paso.
                          </Text>
                        </Box>
                      </HStack>

                      {o.customerFacingSummary && (
                        <Box
                          borderRadius="lg"
                          borderWidth="1px"
                          borderColor="orange.100"
                          bg="white/50"
                          p={4}
                        >
                          <HStack gap={2} alignItems="flex-start">
                            <Box flexShrink={0} mt={1}>
                              <Info
                                size={16}
                                color="var(--chakra-colors-orange-500)"
                              />
                            </Box>
                            <Text
                              fontSize="sm"
                              color="orange.800"
                              lineHeight="tall"
                            >
                              {o.customerFacingSummary}
                            </Text>
                          </HStack>
                        </Box>
                      )}
                    </Box>
                  )}

                  {o.isCompleted ? (
                    <CompletedState
                      applicationStatus={o.applicationStatus}
                      isReturned={o.isReturned}
                      copyLink={o.copyLink}
                      copied={o.copied}
                      handleReset={o.handleReset}
                    />
                  ) : (
                    <>
                      {o.currentStep === 1 && (
                        <Step1BasicInfo
                          data={o.data}
                          erps={o.catalogs.erps}
                          updateData={o.updateData}
                          fieldErrors={o.fieldErrors}
                        />
                      )}
                      {o.currentStep === 2 && (
                        <Step2Profile
                          data={o.data}
                          cities={o.catalogs.cities}
                          updateData={o.updateData}
                          fieldErrors={o.fieldErrors}
                        />
                      )}
                      {o.currentStep === 3 && (
                        <Step3Collection
                          data={o.data}
                          banks={o.catalogs.banks}
                          updateData={o.updateData}
                          fieldErrors={o.fieldErrors}
                        />
                      )}
                      {o.currentStep === 4 && (
                        <Step4Experience
                          data={o.data}
                          updateData={o.updateData}
                          fieldErrors={o.fieldErrors}
                          uploadedDocs={o.uploadedDocs}
                          logoPreviewUrl={o.logoPreviewUrl}
                          handleFileSelect={o.handleFileSelect}
                          handleFileChange={o.handleFileChange}
                          fileInputRefs={o.fileInputRefs}
                        />
                      )}
                      {o.currentStep === 5 && (
                        <Step5Billing
                          data={o.data}
                          updateData={o.updateData}
                          fieldErrors={o.fieldErrors}
                        />
                      )}
                      {o.currentStep === 6 && (
                        <Step6Compliance
                          data={o.data}
                          updateData={o.updateData}
                          fieldErrors={o.fieldErrors}
                          uploadedDocs={o.uploadedDocs}
                          handleFileSelect={o.handleFileSelect}
                          handleFileChange={o.handleFileChange}
                          fileInputRefs={o.fileInputRefs}
                        />
                      )}
                    </>
                  )}
                </>
              )}
            </Box>
          </Box>

          <OnboardingFooter
            currentStep={o.currentStep}
            isCompleted={o.isCompleted}
            isSaving={o.isSaving}
            isUploading={o.isUploading}
            isSubmitting={o.isSubmitting}
            hasApplication={o.hasApplication}
            prevStep={o.prevStep}
            handleContinue={o.handleContinue}
            handleFinalSubmit={o.handleFinalSubmit}
            onNewApplication={o.handleReset}
          />
        </Flex>
      </Flex>

      <EmailModal
        open={o.showEmailModal}
        loading={o.isSaving}
        onConfirm={o.confirmEmailAndStart}
        onClose={() => o.setShowEmailModal(false)}
      />

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        [data-onboarding-error="true"] input,
        [data-onboarding-error="true"] textarea {
          border-color: var(--chakra-colors-red-400) !important;
          box-shadow: 0 0 0 1px var(--chakra-colors-red-400) !important;
        }
      `}</style>
    </Flex>
  );
}
